// Quantization — pure logic, no Tone/audio deps, so it's unit-testable
// (CLAUDE.md §6, docs/04 §4.7).

import type { QuantizeGrid } from "../util/types.ts";

/** Convert a grid subdivision to its length in seconds at a given tempo. */
export function gridToSeconds(grid: QuantizeGrid, bpm: number): number {
  const quarterNoteSec = 60 / bpm;
  switch (grid) {
    case "4n":
      return quarterNoteSec;
    case "8n":
      return quarterNoteSec / 2;
    case "16n":
      return quarterNoteSec / 4;
  }
}

/**
 * Snap a time toward the nearest grid line.
 * @param t        time in seconds
 * @param gridSec  grid spacing in seconds (e.g. a 16th note)
 * @param strength 0..1 — 0 leaves t untouched, 1 hard-snaps to the grid
 */
export function quantizeTime(t: number, gridSec: number, strength = 1): number {
  if (gridSec <= 0) return t;
  const s = clamp01(strength);
  const nearest = Math.round(t / gridSec) * gridSec;
  return t + (nearest - t) * s;
}

/**
 * Quantize the onset of every event. If `loopLengthSec` is given, times that
 * snap to/past the loop end wrap back into [0, loopLength) (docs/04 §4.7).
 * Other event fields are preserved.
 */
export function quantizeEvents<E extends { time: number }>(
  events: readonly E[],
  gridSec: number,
  strength = 1,
  loopLengthSec?: number,
): E[] {
  return events.map((e) => {
    let time = quantizeTime(e.time, gridSec, strength);
    if (loopLengthSec && loopLengthSec > 0) {
      time = ((time % loopLengthSec) + loopLengthSec) % loopLengthSec;
    }
    return { ...e, time };
  });
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
